import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Switch,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/lab';
import Autocomplete from '@mui/material/Autocomplete';
import { useIntl } from 'react-intl';

import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';
import NumberFormatCustom from '../../../../components/formComponents/NumberFormatCustom';
import { ICategoryItem } from '../../categories/interfaces';
import homeStyles from '../../../../constants/homeStyles';
import { IProductType, ITypeParameter } from '../../../../interfaces/productType';
import { useFormatMessage, useShowErrors } from '../../../../hooks';
import { useStylesProductPage } from '../hooks/useStylesProductPage';
import CkEditorCustom from '../../../../components/formComponents/CkEditorCustom';
import { IUser } from '../../../../interfaces/user';
import { ICountry } from '../../../auth/interfaces';

interface IProps {
  id: string | undefined;
  formik: any;
  description: string | undefined;
  setDescEditorState: any;
  setDescEditorStateTranslation: any;
  editLoading: boolean;
  categories: ICategoryItem[] | undefined;
  types: IProductType[] | undefined;
  typeParams: ITypeParameter[] | undefined;
  paramsLoading: boolean;
  me: IUser;
  product: any;
  countries: ICountry[];
  setLanguage: (value: string) => void;
  paramLanguage: string;
  descEditorState: any;
  descEditorStateTranslation: any;
}

const levelsSubCategories = [1, 2, 3, 4, 5];

export const ProductForm: React.FC<IProps> = ({
  id,
  formik,
  description,
  editLoading,
  categories,
  types,
  typeParams,
  paramsLoading,
  setDescEditorState,
  me,
  countries,
}) => {
  const fm = useFormatMessage();
  const navigate = useNavigate();
  const homeClasses = homeStyles();
  const classes1 = useStylesProductPage();
  const { classes } = classes1;
  const intl = useIntl();

  const filterCountries = useMemo(() => {
    if (countries) {
      const newList: ICountry[] = [];
      countries.forEach(i => {
        if (i.id === 1 || i.id === 69 || i.id === 225 || i.id === 20) {
          newList.push({ ...i, favorite: true });
        } else {
          newList.push(i);
        }
      });
      return newList;
    }
    return [];
  }, [countries]);

  const [subCategories, setSubCategories] = useState<ICategoryItem[][]>([]);
  const [categoryTypes, setTypes] = useState<IProductType[]>([]);

  const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } =
    formik;

  const productClass: 'physical' | 'digital' | 'service' = useMemo(() => {
    if (types && values.productTypeId) {
      const type = types.find(item => item.id?.toString() === values.productTypeId?.toString());
      if (type) {
        return type.special_type || 'physical';
      }
    }
    return 'physical';
  }, [types, values.productTypeId]);

  useEffect(() => {
    const newCategoryTypes = categoryTypes.length > 0 ? categoryTypes : types;
    if (newCategoryTypes && values.productTypeId) {
      const type = newCategoryTypes.find(
        item => item.id?.toString() === values.productTypeId?.toString()
      );
      if (!type) {
        setFieldValue('productTypeId', newCategoryTypes[0]?.id || '');
      }
    }
  }, [categoryTypes, types]);

  const getSubCategories = useCallback(
    (level: number) => {
      return subCategories[level];
    },
    [subCategories]
  );

  const showErrorAlect = useMemo(() => {
    if (me.is_admin) return false;
    if (!categories) return false;
    if (values.mainCategoryId && categories) {
      const cat = categories.find(i => i.id?.toString() === values.mainCategoryId?.toString());
      if (cat && !cat.active) return true;
    }
    if (values.categoryId_1 && categories) {
      const categories = getSubCategories(1);
      if (categories) {
        const cat = categories.find(i => i.id?.toString() === values.categoryId_1?.toString());
        if (cat && !cat.active) return true;
      }
    }
    if (values.categoryId_2 && categories) {
      const categories = getSubCategories(2);
      if (categories) {
        const cat = categories.find(i => i.id?.toString() === values.categoryId_2?.toString());
        if (cat && !cat.active) return true;
      }
    }
    if (values.categoryId_3 && categories) {
      const categories = getSubCategories(3);
      if (categories) {
        const cat = categories.find(i => i.id?.toString() === values.categoryId_3?.toString());
        if (cat && !cat.active) return true;
      }
    }
    if (values.categoryId_4 && categories) {
      const categories = getSubCategories(4);
      if (categories) {
        const cat = categories.find(i => i.id?.toString() === values.categoryId_4?.toString());
        if (cat && !cat.active) return true;
      }
    }
    if (values.categoryId_5 && categories) {
      const categories = getSubCategories(5);
      if (categories) {
        const cat = categories.find(i => i.id?.toString() === values.categoryId_5?.toString());
        if (cat && !cat.active) return true;
      }
    }
    return false;
  }, [categories, values, getSubCategories, me]);

  useEffect(() => {
    if (values.mainCategoryId && categories) {
      const newCategories = categories.find(
        item => item.id?.toString() === values.mainCategoryId?.toString()
      );
      if (newCategories && newCategories.children && newCategories.children.length > 0) {
        if (
          subCategories[1] &&
          subCategories[1].length > 0 &&
          subCategories[1][0].id !== newCategories.children[0].id
        ) {
          levelsSubCategories.forEach(level => setFieldValue(`categoryId_${level}`, ''));
        }
        const subCategoriesLvl1 = newCategories.children;
        const subCategoriesLvl2: ICategoryItem | undefined =
          values.categoryId_1 &&
          subCategoriesLvl1?.length &&
          newCategories.children.find(
            item => item.id?.toString() === values.categoryId_1?.toString()
          );
        const subCategoriesLvl3: ICategoryItem | undefined =
          values.categoryId_2 &&
          subCategoriesLvl2?.children?.length &&
          subCategoriesLvl2.children.find(
            item => item.id?.toString() === values.categoryId_2?.toString()
          );
        const subCategoriesLvl4: ICategoryItem | undefined =
          values.categoryId_3 &&
          subCategoriesLvl3?.children?.length &&
          subCategoriesLvl3.children.find(
            item => item.id?.toString() === values.categoryId_3?.toString()
          );
        const subCategoriesLvl5: ICategoryItem | undefined =
          values.categoryId_4 &&
          subCategoriesLvl4?.children?.length &&
          subCategoriesLvl4.children.find(
            item => item.id?.toString() === values.categoryId_4?.toString()
          );
        if (subCategoriesLvl1.length === 0) {
          setFieldValue(`categoryId_1`, '');
          setFieldValue(`categoryId_2`, '');
          setFieldValue(`categoryId_3`, '');
          setFieldValue(`categoryId_4`, '');
          setFieldValue(`categoryId_5`, '');
        }
        if (subCategoriesLvl2?.children?.length === 0) {
          setFieldValue(`categoryId_2`, '');
          setFieldValue(`categoryId_3`, '');
          setFieldValue(`categoryId_4`, '');
          setFieldValue(`categoryId_5`, '');
        }
        if (subCategoriesLvl3?.children?.length === 0) {
          setFieldValue(`categoryId_3`, '');
          setFieldValue(`categoryId_4`, '');
          setFieldValue(`categoryId_5`, '');
        }
        if (subCategoriesLvl4?.children?.length === 0) {
          setFieldValue(`categoryId_4`, '');
          setFieldValue(`categoryId_5`, '');
        }
        if (subCategoriesLvl5?.children?.length === 0) {
          setFieldValue(`categoryId_5`, '');
        }
        setSubCategories(state => {
          const newArr = [...state];
          newArr[1] = subCategoriesLvl1.length ? subCategoriesLvl1 : [];
          newArr[2] = subCategoriesLvl2?.children?.length ? subCategoriesLvl2?.children : [];
          newArr[3] = subCategoriesLvl3?.children?.length ? subCategoriesLvl3?.children : [];
          newArr[4] = subCategoriesLvl4?.children?.length ? subCategoriesLvl4?.children : [];
          newArr[5] = subCategoriesLvl5?.children?.length ? subCategoriesLvl5?.children : [];
          return newArr;
        });
      } else {
        setSubCategories([]);
        setFieldValue(`categoryId_1`, '');
        setFieldValue(`categoryId_2`, '');
        setFieldValue(`categoryId_3`, '');
        setFieldValue(`categoryId_4`, '');
        setFieldValue(`categoryId_5`, '');
      }
    } else {
      setSubCategories([]);
    }
  }, [
    categories,
    values.mainCategoryId,
    values.categoryId_1,
    values.categoryId_2,
    values.categoryId_3,
    values.categoryId_4,
    values.categoryId_5,
  ]);

  const hiddenCategory = (level: number, category: ICategoryItem) => {
    if (me.is_admin) return false;
    if (!category.active) return true;
    const mainCat = categories?.find(
      i => i.id?.toString() === values.mainCategoryId?.toString()
    );
    if (!mainCat) return true;
    if (!mainCat.active) return true;
    if (level > 1) {
      let parentActive = true;
      new Array(level - 1).fill(0).forEach((_, index) => {
        const subCategories = getSubCategories(index + 1);
        const parentCat = subCategories?.find(
          i => i.id?.toString() === values[`categoryId_${index + 1}`]?.toString()
        );
        if (parentCat && !parentCat.active) {
          parentActive = false;
        }
      });
      return !parentActive;
    }
    return false;
  };

  const [submitBtn, setSubmitBtn] = useState(false);
  const handleFormSubmit = (e: any) => {
    handleSubmit(e);
    setSubmitBtn(true);
    setTimeout(() => {
      setSubmitBtn(false);
    }, 1500);
  };

  useShowErrors([
    submitBtn &&
      ((touched.mainCategoryId && errors.mainCategoryId) ||
        (touched.name && errors.name) ||
        (touched.price && errors.price)),
  ]);

  return (
    <>
      <form
        className={homeClasses.classes.form}
        onSubmit={handleFormSubmit}
        style={{ paddingTop: 0 }}
      >
        {showErrorAlect && (
          <Alert style={{ marginTop: 16 }} severity='warning'>
            {fm('PRODUCT.CATEGORY.INACTIVE')}
          </Alert>
        )}
        <div className={classes.skuContainer}>
          <TextField
            type='text'
            label={fm('PRODUCT.EDIT.NAME')}
            margin='normal'
            name='name'
            value={values.name || ''}
            variant='outlined'
            onBlur={handleBlur('name')}
            onChange={handleChange}
            helperText={touched.name && errors.name}
            error={Boolean(touched.name && errors.name)}
          />
        </div>
        <TextField
          type='text'
          label={`${fm('title')} (SEO)`}
          margin='normal'
          name='metadata.title'
          value={values.metadata?.title || ''}
          variant='outlined'
          onChange={handleChange}
        />

        <TextField
          type='text'
          label={`${fm('description')} (SEO)`}
          margin='normal'
          name='metadata.description'
          value={values.metadata?.description}
          variant='outlined'
          multiline
          rows={4}
          onChange={handleChange}
        />
        <TextField
          type='text'
          label={fm('PRODUCT.EDIT.SKU')}
          margin='normal'
          name='sku'
          value={formik.values.sku || ''}
          variant='outlined'
          onBlur={formik.handleBlur('sku')}
          onChange={formik.handleChange}
          // helperText={(formik.touched.name && formik.errors.name) || ''}
          error={Boolean(formik.touched.name && formik.errors.name)}
        />

        <TextField
          select
          label={fm('PRODUCT.EDIT.CATEGORY')}
          margin='normal'
          name='mainCategoryId'
          value={values.mainCategoryId || ''}
          variant='outlined'
          onBlur={handleBlur('mainCategoryId')}
          onChange={e => {
            setFieldValue('mainCategoryId', e.target.value);
            setTypes([]);
          }}
          helperText={touched.mainCategoryId && errors.mainCategoryId}
          error={Boolean(touched.mainCategoryId && errors.mainCategoryId)}
        >
          {categories &&
            categories.map(item => {
              return (
                <MenuItem
                  hidden={!item.active && !me.is_admin}
                  key={item.id?.toString()}
                  value={item.id?.toString()}
                >
                  {item.name}
                </MenuItem>
              );
            })}
        </TextField>

        {levelsSubCategories.map(level => {
          const subCategories = getSubCategories(level);
          if (subCategories && subCategories?.length > 0) {
            return (
              <TextField
                select
                label={`${fm('PRODUCT.EDIT.SUBCATEGORY')} ${level}`}
                margin='normal'
                name={`categoryId_${level}`}
                value={values[`categoryId_${level}`] || ''}
                variant='outlined'
                onBlur={handleBlur(`categoryId_${level}`)}
                onChange={e => {
                  setFieldValue(`categoryId_${level}`, e.target.value);
                  const types = subCategories.find(
                    item => item.id?.toString() === e.target.value
                  )?.types;
                  if (types) {
                    const newCategoryTypes = types.map(i => i.type);
                    setTypes(newCategoryTypes);
                  } else {
                    setTypes([]);
                  }
                }}
                disabled={!values.mainCategoryId}
                helperText={touched[`categoryId_${level}`] && errors.categoryId}
                error={Boolean(touched[`categoryId_${level}`] && errors.categoryId)}
              >
                {subCategories.length > 0 &&
                  subCategories.map(item => (
                    <MenuItem
                      hidden={hiddenCategory(level, item)}
                      key={item.id?.toString()}
                      value={item.id?.toString()}
                    >
                      {item.name}
                    </MenuItem>
                  ))}
              </TextField>
            );
          }
          return <></>;
        })}
        <FormControlLabel
          control={
            <Switch
              className={homeClasses.classes.switch}
              size='small'
              name='active'
              checked={values.active}
              onChange={handleChange}
            />
          }
          label={fm('PRODUCT.EDIT.STATUS')}
        />

        <InputLabel shrink>{`${fm('PRODUCT.TABLE.DESCRIPTION')}`}</InputLabel>
        <CkEditorCustom html={description || ''} setEditorStateCallback={setDescEditorState} />

        {types && types.length > 0 && (
          <TextField
            select
            margin='normal'
            label={fm('PRODUCT.EDIT.TYPE')}
            value={values.productTypeId || ''}
            onChange={handleChange}
            name='productTypeId'
            variant='outlined'
            error={Boolean(touched.productTypeId && errors.productTypeId)}
            className={classes.labelDesc}
          >
            {(categoryTypes.length > 0 ? categoryTypes : types).map(option => (
              <MenuItem key={option.id?.toString()} value={option.id?.toString() || ''}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <div className={classes.containerStock}>
          {productClass === 'physical' && (
            <TextField
              type='text'
              label={fm('PRODUCT.EDIT.WEIGHT')}
              margin='normal'
              name='weight'
              value={values.weight}
              variant='outlined'
              onBlur={handleBlur('weight')}
              onChange={handleChange}
              helperText={touched.weight && errors.weight}
              error={Boolean(touched.weight && errors.weight)}
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              InputLabelProps={{ shrink: true }}
              // style={{ width: '32.5%' }}
            />
          )}
        </div>

        {productClass === 'physical' && (
          <div className={classes.containerStock}>
            <TextField
              type='text'
              label={fm('PRODUCT.EDIT.LENGTH')}
              margin='normal'
              name='length'
              value={values.length}
              variant='outlined'
              onBlur={handleBlur('length')}
              onChange={handleChange}
              helperText={touched.length && errors.length}
              error={Boolean(touched.length && errors.length)}
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              InputLabelProps={{ shrink: true }}
              style={{ width: '32.5%' }}
            />
            <TextField
              type='text'
              label={fm('PRODUCT.EDIT.WIDTH')}
              margin='normal'
              name='width'
              value={values.width}
              variant='outlined'
              onBlur={handleBlur('width')}
              onChange={handleChange}
              helperText={touched.width && errors.width}
              error={Boolean(touched.width && errors.width)}
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              InputLabelProps={{ shrink: true }}
              style={{ width: '32.5%' }}
            />
            <TextField
              type='text'
              label={fm('PRODUCT.EDIT.HEIGHT')}
              margin='normal'
              name='height'
              value={values.height}
              variant='outlined'
              onBlur={handleBlur('height')}
              onChange={handleChange}
              helperText={touched.height && errors.height}
              error={Boolean(touched.height && errors.height)}
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              InputLabelProps={{ shrink: true }}
              style={{ width: '32.5%' }}
            />
          </div>
        )}

        {paramsLoading
          ? Array.from(Array(3)).map((_, idx) => (
              <Skeleton
                key={idx}
                width='100%'
                height={86}
                animation='wave'
                style={{ marginTop: -5, marginBottom: -5 }}
              />
            ))
          : typeParams?.map(item => {
              if (item.type === 'number') {
                return (
                  <TextField
                    type='text'
                    label={item.name}
                    margin='normal'
                    name={`parameter${item.id}`}
                    value={values[`parameter${item.id}`] || ''}
                    variant='outlined'
                    onBlur={handleBlur(`parameter${item.id}`)}
                    onChange={handleChange(`parameter${item.id}`)}
                    disabled={!me.is_admin}
                    helperText={
                      touched[`parameter${item.id}`] && errors[`parameter${item.id}`]
                    }
                    error={Boolean(
                      touched[`parameter${item.id}`] && errors[`parameter${item.id}`]
                    )}
                    key={item.id}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputComponent:
                        item.name !== 'Год' ? (NumberFormatCustom as any) : undefined,
                    }}
                  />
                );
              }
              if (item.type === 'enum') {
                return (
                  <>
                    <Autocomplete
                      options={item.parameter_enum_values?.sort((a, b) => 1) || []}
                      autoHighlight
                      style={{ marginTop: 16 }}
                      value={item.parameter_enum_values?.find(
                        i => i.value === values[`parameter${item.id}`]
                      )}
                      onChange={(_event: any, newValue) => {
                        setFieldValue(`parameter${item.id}`, newValue?.value || '');
                      }}
                      groupBy={i =>
                        intl.formatMessage({
                          id: 'OTHER.VARIANTS',
                        })
                      }
                      noOptionsText={intl.formatMessage({ id: 'CATEGORIES.TABLE.EMPTY' })}
                      loadingText={intl.formatMessage({ id: 'COMMON.LOADING' })}
                      // disableClearable
                      getOptionLabel={option => (option.value ? option.value : '')}
                      renderOption={(props, option) => (
                        <Box
                          component='li'
                          sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                          {...props}
                          value={option.value}
                        >
                          {option.value}
                        </Box>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={item.name}
                          variant='outlined'
                          inputProps={{
                            ...params.inputProps,
                            style: { fontFamily: 'Open Sans' },
                            autoComplete: 'new-password', // disable autocomplete and autofill
                          }}
                        />
                      )}
                    />
                  </>
                );
              }

              if (item.type === 'string') {
                return (
                  <div className={classes.containerName} key={item.id}>
                    <TextField
                      type='text'
                      variant='outlined'
                      margin='normal'
                      label={`${item.name}`}
                      name={`parameter${item.id}`}
                      value={values[`parameter${item.id}`] || ''}
                      onBlur={handleBlur(`parameter${item.id}`)}
                      onChange={handleChange(`parameter${item.id}`)}
                      disabled={!me.is_admin}
                      helperText={
                        touched[`parameter${item.id}`] && errors[`parameter${item.id}`]
                      }
                      error={Boolean(
                        touched[`parameter${item.id}`] && errors[`parameter${item.id}`]
                      )}
                      className={classes.fieldName}
                    />
                  </div>
                );
              }

              if (item.type === 'text') {
                return (
                  <>
                    <TextField
                      type='text'
                      label={`${item.name}`}
                      margin='normal'
                      name={`parameter${item.id}`}
                      value={values[`parameter${item.id}`] || ''}
                      disabled={!me.is_admin}
                      helperText={
                        touched[`parameter${item.id}`] && errors[`parameter${item.id}`]
                      }
                      error={Boolean(
                        touched[`parameter${item.id}`] && errors[`parameter${item.id}`]
                      )}
                      variant='outlined'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      multiline
                      rows={4}
                    />
                  </>
                );
              }

              if (item.type === 'multiple_enum') {
                return (
                  <>
                    <Autocomplete
                      options={item.parameter_enum_values?.sort((a, b) => 1) || []}
                      multiple
                      autoHighlight
                      style={{ marginTop: 16 }}
                      value={
                        values[`parameter${item.id}`]
                          ? values[`parameter${item.id}`].map((i: string) => {
                              const find = item.parameter_enum_values?.find(
                                el => el.value === i
                              );
                              return find;
                            })
                          : []
                      }
                      onChange={(_event: any, newValue) => {
                        newValue &&
                          setFieldValue(
                            `parameter${item.id}`,
                            newValue.map(i => i?.value)
                          );
                      }}
                      groupBy={i =>
                        intl.formatMessage({
                          id: i?.favorite ? 'POPULAR.VARIANTS' : 'OTHER.VARIANTS',
                        })
                      }
                      noOptionsText={intl.formatMessage({ id: 'CATEGORIES.TABLE.EMPTY' })}
                      loadingText={intl.formatMessage({ id: 'COMMON.LOADING' })}
                      disableClearable
                      getOptionLabel={option => (option && option.value ? option.value : '')}
                      renderOption={(props, option) => (
                        <Box
                          component='li'
                          sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                          {...props}
                          value={option?.value || ''}
                        >
                          {option.value}
                        </Box>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={item.name}
                          variant='outlined'
                          inputProps={{
                            ...params.inputProps,
                            style: { fontFamily: 'Open Sans' },
                            autoComplete: 'new-password', // disable autocomplete and autofill
                          }}
                        />
                      )}
                    />
                  </>
                );
              }

              if (item.type === 'country') {
                return (
                  <>
                    <Autocomplete
                      id='country-select-demo'
                      options={filterCountries.sort((a, b) =>
                        // eslint-disable-next-line no-nested-ternary
                        a.favorite === b.favorite ? 0 : a.favorite ? -1 : 1
                      )}
                      autoHighlight
                      style={{ marginTop: 16 }}
                      value={filterCountries.find(
                        i => i.name === values[`parameter${item.id}`]
                      )}
                      onChange={(_event: any, newValue) => {
                        newValue && setFieldValue(`parameter${item.id}`, newValue.name);
                      }}
                      groupBy={i =>
                        intl.formatMessage({ id: i.favorite ? 'FAVORITES' : 'OTHER' })
                      }
                      noOptionsText={intl.formatMessage({ id: 'CATEGORIES.TABLE.EMPTY' })}
                      loadingText={intl.formatMessage({ id: 'COMMON.LOADING' })}
                      disableClearable
                      getOptionLabel={option => option.ru_name}
                      renderOption={(props, option) => (
                        <Box
                          component='li'
                          sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                          {...props}
                          value={option.id}
                        >
                          {option.ru_name}
                        </Box>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={item.name}
                          variant='outlined'
                          inputProps={{
                            ...params.inputProps,
                            style: { fontFamily: 'Open Sans' },
                            autoComplete: 'new-password', // disable autocomplete and autofill
                          }}
                        />
                      )}
                    />
                  </>
                );
              }
            })}
        <div className={classes.actions}>
          <Button
            onClick={() => navigate(-1)}
            className={classes.buttons}
            variant='contained'
            color='success'
          >
            {fm('COMMON.BUTTON.CLOSE')}
          </Button>
          <ButtonWithLoader marginLeft={10} disabled={editLoading} loading={editLoading}>
            {id ? fm('COMMON.BUTTON.SAVE') : fm('PRODUCT.BUTTON.ADD')}
          </ButtonWithLoader>
        </div>
      </form>
    </>
  );
};
